import type { EyePrescription, OpticalJobPrescription } from '@optiflow/types';
import type { EyeForm } from '@/modules/medical/examinationForm';
import type { ExternalPrescriptionForm } from '../builder/draft';

/** One eye as the grid shows it: every recorded value as text, a missing one as a blank. */
export function eyeFormOf(eye: EyePrescription): EyeForm {
  return {
    sphere: eye.sphere ?? '',
    cylinder: eye.cylinder ?? '',
    axis: eye.axis === null ? '' : String(eye.axis),
    add: eye.add ?? '',
    prism: eye.prism ?? '',
    prismBase: eye.prismBase ?? '',
  };
}

/**
 * An external prescription as the job holds it, put back into the form it was typed in, so a correction starts from exactly what was recorded.
 *
 * The PD keeps its form: a binocular PD stays binocular and a monocular pair stays monocular. Nothing is derived from anything else.
 */
export function externalFormOf(prescription: OpticalJobPrescription): ExternalPrescriptionForm {
  const monocular =
    prescription.totalPdMm === null &&
    (prescription.rightPdMm !== null || prescription.leftPdMm !== null);
  return {
    right: eyeFormOf(prescription.right),
    left: eyeFormOf(prescription.left),
    pdMode: monocular ? 'MONOCULAR' : 'BINOCULAR',
    totalPdMm: prescription.totalPdMm ?? '',
    rightPdMm: prescription.rightPdMm ?? '',
    leftPdMm: prescription.leftPdMm ?? '',
    pdType: prescription.pdType ?? '',
    externalReference: prescription.externalReference ?? '',
    prescriberName: prescription.prescriberName ?? '',
    prescribedOn: prescription.prescribedOn ?? '',
    expiresOn: prescription.expiresOn ?? '',
    // What the job recorded is kept as it was: a correction is never refilled from a saved measurement.
    pdSource: null,
    pdManual: true,
  };
}
